import { isUndefined } from 'lodash-es';

import { TimeseriesClosestPoint } from './find-closest-points.js';
import type { ChartUnit } from '../../../../../core/types/chart-unit.js';
import { GroupDataPoint } from '../../../../../core/types/data-point.js';
import { Formatter } from '../../../../../core/types/formatter.js';
import { TooltipItem } from '../../../../../core/types/tooltip.js';
import { defaultUnitFormatter } from '../../../../../core/utils/defaultUnitFormatter.js';
import { safeGet } from '../../../../../core/utils/safe-get.js';
import { WeakMapDatapoints } from '../../../../types/timeseries.js';

const buildBandDataPointValue = (
  y0: number,
  y1: number,
  unit: ChartUnit,
  formatter: Formatter,
  originalUnit: ChartUnit,
  absoluteY0?: number,
  absoluteY1?: number,
) => {
  const [min, max] = [y0, y1].sort((a, b) => a - b);

  const result = [`${formatter(min, unit)} – ${formatter(max, unit)}`];

  if (absoluteY0 !== undefined && absoluteY1 !== undefined) {
    const [absoluteMin, absoluteMax] = [absoluteY0, absoluteY1].sort(
      (a, b) => a - b,
    );

    result.unshift(
      `${formatter(absoluteMin, originalUnit)} – ${formatter(
        absoluteMax,
        originalUnit,
      )}`,
    );
  }

  return result;
};

const buildValuesBasedOnValueRepresentation = (
  value: number,
  unit: ChartUnit,
  originalUnit: ChartUnit,
  formatter: Formatter,
  absoluteValue?: number,
) => {
  const isRelativeRepresentation = absoluteValue !== undefined;

  if (isRelativeRepresentation) {
    return [
      defaultUnitFormatter(originalUnit)(absoluteValue),
      formatter(value),
    ];
  }

  return [formatter(value, unit)];
};

const tooltipItemsMemoized = new Map<string, AsyncValuesTooltipItem>();

type AsyncValuesTooltipItem = TooltipItem & { asyncValues: () => string[] };

export const toTooltipItems = (
  xPoints: TimeseriesClosestPoint[],
  formatters: Formatter[],
  datapointsMap: WeakMapDatapoints,
  units: ChartUnit[],
): AsyncValuesTooltipItem[] => {
  const tooltipItems: AsyncValuesTooltipItem[] = [];
  const bandSeries: Record<string, boolean> = {};
  const [leftUnit] = units;
  const [leftFormatter, rightFormatter] = formatters;

  xPoints.forEach((xPoint, index) => {
    const xPointId = `${xPoint.id}-${index}-${xPoint.y}-${xPoint.x}`;
    let tooltipItem: AsyncValuesTooltipItem;
    const memoedTooltipItem = tooltipItemsMemoized.get(xPointId);

    if (memoedTooltipItem) {
      tooltipItem = memoedTooltipItem;
    } else {
      const datapointMetadata = safeGet(datapointsMap, xPoint.datapoint);

      const {
        seriesId,
        value,
        absoluteValue,
        y0,
        absoluteY0,
        y1,
        absoluteY1,
        color,
        unit,
        originalUnit,
        seriesName,
        variant,
      } = datapointMetadata;
      const formatter = unit === leftUnit ? leftFormatter : rightFormatter;

      const baseTooltipItem = {
        id: `${seriesId}-${index}`,
        name: seriesName,
        color,
        datapoint: xPoint.datapoint,
      };

      if (variant === 'band') {
        if (!bandSeries[xPoint.id]) {
          tooltipItems.push({
            ...baseTooltipItem,
            asyncValues: () =>
              buildBandDataPointValue(
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                y0!,
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                y1!,
                unit,
                formatter,
                originalUnit,
                absoluteY0,
                absoluteY1,
              ),
          });

          bandSeries[xPoint.id] = true;

          return;
        }

        bandSeries[xPoint.id] = false;

        return;
      }

      tooltipItem = {
        ...baseTooltipItem,
        asyncValues: () =>
          buildValuesBasedOnValueRepresentation(
            value ?? 0,
            unit,
            originalUnit ? originalUnit : '',
            formatter,
            absoluteValue,
          ),
      };
      tooltipItemsMemoized.set(xPointId, tooltipItem);
    }

    tooltipItems.push(tooltipItem);
  });

  return tooltipItems;
};

/**
 * Returns the items displayed in the tooltip
 * @param closestPoint - the closest point
 * @param points - array of all points with same x coordinate of the closest point
 * @param groupedPoints - grouped points by threshold
 */
export function filterGroupedPoints(
  closestPoint: TimeseriesClosestPoint,
  points: TimeseriesClosestPoint[],
  groupedPoints: GroupDataPoint,
): TimeseriesClosestPoint[] {
  const closestPointGroupIds = groupedPoints
    .filter((group) => group.map((g) => g.id).includes(closestPoint.id))
    .flatMap((group) => group.map((point) => point.id));

  return points.filter((point) => closestPointGroupIds.includes(point.id));
}

/**
 * Returns only timeframe points or only timestamp points depending on the type (timeframe or timestamp) of the closest point.
 * @param points - array of all points with same x coordinate of the closest point
 * @param closestPoint - the closest point
 */
export function filterTimestampsAndTimeframes(
  points: TimeseriesClosestPoint[],
  closestPoint: TimeseriesClosestPoint,
): TimeseriesClosestPoint[] {
  const filterTimestamps = (point: TimeseriesClosestPoint) =>
    isUndefined(point.datapoint.end);
  const filterTimeframes = (point: TimeseriesClosestPoint) =>
    !isUndefined(point.datapoint.end);
  const hasTimestampClosestPoint = isUndefined(closestPoint.datapoint.end);

  return hasTimestampClosestPoint
    ? points.filter(filterTimestamps)
    : points.filter(filterTimeframes);
}
